import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';

interface MetricCardProps {
    label: string;
    value: number;
    severity?: 'critical' | 'high' | 'medium' | 'low';
    topClients?: Array<{ name: string; count: number }>;
}

const MetricCard: React.FC<MetricCardProps> = ({
                                                   label,
                                                   value,
                                                   severity = 'low',
                                                   topClients = []
                                               }) => {
    const theme = useTheme();

    const getSeverityColor = () => {
        switch (severity) {
            case 'critical':
                return theme.palette.severity.critical;
            case 'high':
                return theme.palette.severity.high;
            case 'medium':
                return theme.palette.severity.medium; // Amber - between warning and success
            case 'low':
            default:
                return theme.palette.severity.low;
        }
    };

    return (
        <Box
            sx={{
                backgroundColor: theme.palette.background.secondary,
                border: `1px solid ${theme.palette.border.main}`,
                borderRadius: 1,
                padding: 3,
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                minHeight: '140px',
                '&:hover': {
                    borderColor: theme.palette.primary.main,
                    transform: 'translateY(-2px)',
                },
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                {/* Left side - Label and Value */}
                <Box>
                    <Typography
                        sx={{
                            fontSize: '15px',
                            color: theme.palette.text.tertiary,
                            fontWeight: 500,
                            marginBottom: 1.5,
                        }}
                    >
                        {label}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '32px',
                            fontWeight: 700,
                            color: getSeverityColor(),
                        }}
                    >
                        {value}
                    </Typography>
                </Box>

                {/* Right side - Top Clients */}
                {topClients.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: '160px' }}>
                        {topClients.map((client, index) => (
                            <Box
                                key={index}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: 1, minWidth: 0 }}>
                                    {/* Colored dot indicator */}
                                    <Box
                                        sx={{
                                            width: '7px',
                                            height: '7px',
                                            borderRadius: '50%',
                                            backgroundColor: getSeverityColor(),
                                            opacity: 0.6,
                                            flexShrink: 0,
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: '12px',
                                            color: theme.palette.text.secondary,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        {client.name}
                                    </Typography>
                                </Box>
                                {/* Badge-style count */}
                                <Box
                                    sx={{
                                        backgroundColor: theme.palette.glow,
                                        borderRadius: '10px',
                                        padding: '3px 8px',
                                        minWidth: '24px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: '11px',
                                            color: theme.palette.text.secondary,
                                            fontWeight: 600,
                                            lineHeight: 1,
                                        }}
                                    >
                                        {client.count}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default MetricCard;